import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { AUDIENCES, IMG } from "../data/content";
import Seo from "../components/Seo";

const IMAGES = {
  "traditional-owners": IMG.ownerPractitioner,
  "rangers": IMG.handsSeeds,
  "mining": IMG.polytunnel,
  "practitioners": IMG.seedsBowl,
  "funders": IMG.truck,
};

export default function Audience() {
  const { slug } = useParams();
  const audience = AUDIENCES.find((a) => a.slug === slug);
  if (!audience) return <Navigate to="/" replace />;

  return (
    <div data-testid={`page-audience-${audience.slug}`}>
      <Seo
        title={`For ${audience.label}s`}
        description={audience.promise}
        path={`/audience/${audience.slug}`}
      />
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <Link to="/" className="link-mono text-eucalypt inline-flex items-center gap-2 mb-10" data-testid="back-home">
            <ArrowLeft size={14} /> All Pathways
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <p className="specimen-label mb-4">Pathway No. {audience.no} — {audience.label}</p>
              <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98]">
                For <em className="text-eucalypt">{audience.label.toLowerCase()}.</em>
              </h1>
              <p className="mt-8 text-base md:text-lg text-ink/85 leading-relaxed max-w-2xl">
                {audience.promise}
              </p>
            </div>
            <div className="lg:col-span-5">
              <img src={IMAGES[audience.slug]} alt={audience.label} className="w-full h-[440px] object-cover border border-rule p-2 bg-cream" />
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-rule bg-parchment/40">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <p className="section-num mb-3">How we work with you</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl mb-10 leading-tight">
            Where this pathway typically starts.
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-px bg-rule border border-rule">
            {audience.bullets.map((b, i) => (
              <li key={b} className="bg-card p-7 md:p-8 flex gap-4 items-start" data-testid={`pathway-bullet-${i}`}>
                <Check className="text-eucalypt flex-shrink-0 mt-1" size={18} />
                <p className="font-serif text-xl md:text-2xl leading-snug">{b}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section>
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-24 text-center">
          <p className="section-num mb-6">Next</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl mb-10 leading-tight">
            Start a conversation tailored to your context.
          </h2>
          <Link
            to="/contact"
            state={{ audience: audience.label }}
            className="inline-flex items-center gap-3 bg-eucalypt text-chalk px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-eucalypt-dark rounded-full hover:-translate-y-0.5 transition-all"
            data-testid="audience-contact-cta"
          >
            Contact us — {audience.label}
          </Link>
        </div>
      </section>
    </div>
  );
}
